import { useEffect, useState } from 'react';
/* eslint-disable no-nested-ternary */
import { DeleteOutlined } from '@ant-design/icons';
import { useIsMutating } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { booleanOptions, numberRegEx } from '@app/constants';
import { hasAllowedRole } from '@app/utils';
import { Namespace } from '@data/index';
import {
  AnswerType,
  QuestionDependencyView,
  QuestionView,
  DependencyType,
  QuestionDependencyBody,
  Role,
} from '@data/sdk/match';
import { Button, AntdForm, AntdInput, AntdSelect, AntdSpace, AntdTypography } from '@ui/components';
import { getProcessorOptions } from '../../constants';
import { useQuestionsList } from '../../hooks';
import {
  getHiddenFormInitialValues,
  getHiddenFormInitialValuesFromQuestion,
  getQuestionsByDependencyType,
} from './utils';

const { Text } = AntdTypography;

type DependencyFormProps = {
  dependency?: QuestionDependencyView;
  onCancel: () => void;
  onDelete?: () => void;
  onSave: (formValues: QuestionDependencyBody) => void;
  questionAnswerType?: AnswerType;
  questionText: string;
  similarQuestionDependency?: QuestionView;
};

type DependencyFormValues = {
  parameter: string | boolean;
  source: { label: string; value: string };
  type: DependencyType;
};

export const HiddenQuestionDependencyForm = ({
  questionText,
  onCancel,
  dependency,
  onSave,
  onDelete,
  questionAnswerType,
  similarQuestionDependency,
}: DependencyFormProps) => {
  const { t } = useTranslation([Namespace.ADMIN]);
  const isMutating = !!useIsMutating();
  const IS_REVIEWER = hasAllowedRole([Role.Reviewer]);

  const [form] = AntdForm.useForm<DependencyFormValues>();
  const type = AntdForm.useWatch('type', form);

  const [sourceAnswerType, setSourceAnswerType] = useState<AnswerType | null>(null);

  const { questionOptions, loadMoreQuestions, handleSearchChange, questionsLoading } =
    useQuestionsList([getQuestionsByDependencyType(type) as AnswerType].filter(Boolean));

  const handleCreateDependency = (formValues: DependencyFormValues) => {
    const createDependencyValues: QuestionDependencyBody = {
      type: formValues.type,
      targetId: dependency?.targetId,
      sourceId: formValues.source.value,
      parameters: [formValues.parameter.toString()],
    };

    onSave(createDependencyValues);
  };

  const handleScroll = (e: any) => {
    if (e.target.scrollTop + e.target.offsetHeight + 2 > e.target.scrollHeight) {
      loadMoreQuestions();
    }
  };

  const initialValues = dependency
    ? getHiddenFormInitialValues(dependency)
    : similarQuestionDependency
    ? getHiddenFormInitialValuesFromQuestion(similarQuestionDependency)
    : undefined;

  useEffect(() => {
    form.resetFields();
  }, [questionAnswerType, similarQuestionDependency]);

  useEffect(() => {
    setSourceAnswerType(null);
  }, [type]);

  const onSourceQuestionSelect = (answerType: AnswerType) => {
    if (answerType !== sourceAnswerType) {
      setSourceAnswerType(answerType);
      form.resetFields(['parameter']);
    }
  };

  return (
    <div className="flex flex-col gap-7 items-center w-full">
      {onDelete && (
        <Button
          icon={<DeleteOutlined />}
          danger
          ghost
          type="text"
          className="ml-auto"
          onClick={onDelete}
          disabled={isMutating || IS_REVIEWER}
        >
          {t('question_page.details.dependencies.delete_dependence')}
        </Button>
      )}
      <AntdForm
        form={form}
        layout="vertical"
        autoComplete="off"
        requiredMark={false}
        onFinish={handleCreateDependency}
        className="w-full flex flex-col items-center"
        initialValues={initialValues}
      >
        <AntdSpace orientation="vertical" size={2} className="items-center">
          <AntdSpace
            orientation="vertical"
            className="p-4 bg-base-100 rounded-default border-base-300 border-solid w-[450px]"
          >
            <AntdSpace orientation="vertical" size="middle" className="w-full">
              <div className="flex flex-col">
                <Text>{t('question_page.details.dependencies.processor')}</Text>
                <AntdForm.Item
                  name="type"
                  rules={[{ required: true, message: t('patient:validations.please_select') }]}
                  className="mb-0"
                >
                  <AntdSelect
                    className="w-full max-w-full"
                    options={
                      questionAnswerType === AnswerType.Boolean
                        ? getProcessorOptions().filter(
                            (el) =>
                              el.value === DependencyType.EqualTo ||
                              el.value === DependencyType.GreaterThan,
                          )
                        : getProcessorOptions().filter(
                            (el) =>
                              el.value === DependencyType.DateToMonths ||
                              el.value === DependencyType.DateToYears,
                          )
                    }
                    onChange={() => form.resetFields(['parameter', 'source'])}
                    disabled={!!dependency}
                  />
                </AntdForm.Item>
              </div>

              <div className="flex flex-col">
                <Text>{t('question_page.details.dependencies.parameter')}</Text>
                {type === DependencyType.GreaterThan ? (
                  <AntdForm.Item
                    name="parameter"
                    rules={[{ required: true, message: t('patient:validations.required') }]}
                    className="mb-0"
                  >
                    <AntdInput className="w-full max-w-full" />
                  </AntdForm.Item>
                ) : type === DependencyType.EqualTo ? (
                  <>
                    {sourceAnswerType === AnswerType.Boolean ? (
                      <AntdForm.Item
                        name="parameter"
                        rules={[
                          {
                            required: sourceAnswerType === AnswerType.Boolean,
                            message: t('patient:validations.please_select'),
                          },
                        ]}
                        className="mb-0"
                      >
                        <AntdSelect options={booleanOptions()} />
                      </AntdForm.Item>
                    ) : (
                      <AntdForm.Item
                        name="parameter"
                        rules={[
                          {
                            required:
                              sourceAnswerType === AnswerType.String ||
                              sourceAnswerType === AnswerType.Number,
                            message:
                              sourceAnswerType === AnswerType.Number
                                ? t('patient:validations.should_be_number')
                                : t('patient:validations.required'),

                            pattern:
                              sourceAnswerType === AnswerType.Number ? numberRegEx : undefined,
                          },
                        ]}
                        className="mb-0"
                      >
                        <AntdInput className="w-full max-w-full" />
                      </AntdForm.Item>
                    )}
                  </>
                ) : (
                  <AntdForm.Item
                    name="parameter"
                    rules={[{ required: true, message: t('patient:validations.required') }]}
                    className="mb-0"
                  >
                    <AntdSelect
                      showSearch
                      placeholder={t('patient:common.type_here')}
                      filterOption={false}
                      allowClear
                      className="w-full max-w-full"
                      options={questionOptions}
                      onSearch={handleSearchChange}
                      onPopupScroll={handleScroll}
                      optionLabelProp="label"
                      loading={questionsLoading}
                      disabled={!!dependency}
                    />
                  </AntdForm.Item>
                )}
              </div>

              <div className="flex flex-col">
                <Text>{t('question_page.details.dependencies.select_question')}:</Text>
                <AntdForm.Item
                  name="source"
                  rules={[{ required: true, message: t('patient:validations.please_select') }]}
                  className="mb-0"
                >
                  <AntdSelect
                    showSearch
                    placeholder={t('patient:common.type_here')}
                    filterOption={false}
                    allowClear
                    options={questionOptions}
                    onSearch={handleSearchChange}
                    onPopupScroll={handleScroll}
                    optionLabelProp="label"
                    loading={questionsLoading}
                    disabled={!!dependency}
                    onChange={(_, o) => form.setFieldValue('source', o)}
                    className="w-full max-w-full"
                    onSelect={(_, option) =>
                      type === DependencyType.EqualTo
                        ? onSourceQuestionSelect(option.answerType)
                        : undefined
                    }
                  />
                </AntdForm.Item>
              </div>
            </AntdSpace>
          </AntdSpace>
          <div className="w-[1px] h-6 bg-base-600" />

          <AntdForm.Item rules={[{ required: true, message: t('patient:validations.required') }]}>
            <div className="p-4 bg-base-100 rounded-default border-solid border-primary-600">
              <Text className="text-xl font-medium">{questionText}</Text>
            </div>
          </AntdForm.Item>
        </AntdSpace>
        <div className="bg-base-100 py-2 w-full flex items-center justify-center gap-4 border-solid border-base-300 border-0 border-t mt-6">
          <Button type="primary" htmlType="submit" loading={isMutating} disabled={IS_REVIEWER}>
            {t('patient:common.save_changes')}
          </Button>
          <Button onClick={onCancel} disabled={isMutating}>
            {t('patient:common.cancel')}
          </Button>
        </div>{' '}
      </AntdForm>
    </div>
  );
};
